import {
  DocumentServicesObject,
  CompRef,
  AppData,
  PageRef,
} from '@wix/document-services-types';
import { ComponentType } from '@wix/ambassador-devcenter-components-v1-component/types';
import { PlatformContext } from '../../types/platformApi';
import { EditorReactComponent } from '@wix/component-protocol/schema';

export function createAppNavigationService(
  context: PlatformContext,
  documentServicesAPI: DocumentServicesObject,
) {
  const compRefToPageRef = (compRef: CompRef): PageRef => ({
    ...compRef,
    pageId: compRef.id,
  });

  const navigateSelectAndScroll = (
    appData: AppData,
    compRef?: CompRef | null,
    pageRef?: PageRef,
  ) => {
    if (pageRef) {
      context.platformApiMethods.document.pages.navigateTo(appData, '', {
        pageRef,
      });
    }

    if (!compRef) {
      return;
    }

    context.platformApiMethods.editor.selection.locateAndHighlightComponentByCompRef(
      appData,
      '',
      { compRef },
    );
  };

  const legacyCheckForCompData = (appData: AppData) => {
    const compData = documentServicesAPI.tpa.app.getFirstAppCompPageId(
      appData.appDefinitionId!,
      true,
    );
    return compData?.pageId ? compData : undefined;
  };

  const navigateToComponentFromCompData = async (
    appData: AppData,
    compData: {
      pageId: string;
      compId: string;
    },
  ) => {
    const { pageId, compId } = compData;
    const compRef = documentServicesAPI.components.get.byId(compId, pageId);
    const pageRef = compRefToPageRef(
      documentServicesAPI.pages.getReference(pageId),
    );
    navigateSelectAndScroll(appData, compRef, pageRef);
  };

  const findApplicationPage = (appDefId: string) => {
    const applicationPages = documentServicesAPI.pages
      .getPagesData()
      .filter(({ managingAppDefId }) => managingAppDefId === appDefId);

    const pageRef = applicationPages[0]?.id
      ? compRefToPageRef(
          documentServicesAPI.pages.getReference(applicationPages[0].id),
        )
      : undefined;

    return pageRef;
  };

  const findWidgetToNavigate = (appDefId: string): CompRef | undefined => {
    const appData = documentServicesAPI.platform.getAppDataByAppDefId(appDefId);
    const allComponents = documentServicesAPI.components.getAllComponents();

    let foundCompRef: CompRef | undefined;

    appData?.components?.find((component) => {
      const isWidgetType =
        component.type === ComponentType.WIDGET_OUT_OF_IFRAME ||
        component.type === ComponentType.CUSTOM_ELEMENT_WIDGET ||
        component.type === ComponentType.EDITOR_REACT_COMPONENT;

      if (!isWidgetType || !component.componentId) {
        return false;
      }

      foundCompRef = allComponents.find((compRef) => {
        if (component.type === ComponentType.EDITOR_REACT_COMPONENT) {
          const compType = documentServicesAPI.components.getType(compRef);
          return compType === (component.data as EditorReactComponent).type;
        } else {
          const compData = documentServicesAPI.components.data.get(compRef);
          return compData?.widgetId === component.componentId;
        }
      });

      return !!foundCompRef;
    });

    return foundCompRef;
  };

  const navigateToWidgetComponent = async (
    appData: AppData,
    matchingCompRef: CompRef,
  ) => {
    const pageRef = compRefToPageRef(
      documentServicesAPI.components.getPage(matchingCompRef),
    );
    navigateSelectAndScroll(appData, matchingCompRef, pageRef);
  };

  const navigateToAppContent = async (appDefId: string) => {
    const appData = documentServicesAPI.platform.getAppDataByAppDefId(appDefId);
    if (!appData) {
      return;
    }

    const compData = legacyCheckForCompData(appData);
    if (compData) {
      return navigateToComponentFromCompData(appData, compData);
    }

    const applicationPageRef = findApplicationPage(appDefId);
    if (applicationPageRef) {
      navigateSelectAndScroll(appData, undefined, applicationPageRef);
      context.platformApiMethods.editor.openPagePanel(appData, '', {
        pageRef: applicationPageRef,
      });
      return;
    }

    const widgetCompRef = findWidgetToNavigate(appDefId);
    if (widgetCompRef) {
      await navigateToWidgetComponent(appData, widgetCompRef);
      context.platformApiMethods.editor.deeplink.show(appData, '', {
        type: 'addPanel',
        params: [appDefId],
      });
      return;
    }
  };

  return {
    openApp: async (appDefId: string) => {
      await navigateToAppContent(appDefId);
    },
  };
}
